import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Clock, CheckCircle2, XCircle } from "lucide-react";

export default function Approvals() {
  const [tab, setTab] = useState("pending");
  return (
    <div className="space-y-6 animate-fade-in" data-testid="approvals-page">
      <div>
        <div className="data-label">Queue</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Approvals</h1>
      </div>
      <div className="flex gap-1 border-b border-border">
        {[
          { id: "pending", label: "Pending" },
          { id: "history", label: "History" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`approvals-tab-${t.id}`}
                  className={`px-4 py-2 text-sm font-semibold uppercase tracking-widest border-b-2 -mb-px ${
                    tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground"
                  }`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "pending" ? <PendingTab /> : <HistoryTab />}
    </div>
  );
}

function PendingTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["approvals-pending"],
    queryFn: async () => (await api.get("/approvals/pending")).data,
  });

  const decide = async (group_id, approve) => {
    const remark = window.prompt(approve ? "Optional remark:" : "Reason for reject:") ?? "";
    if (!approve && !remark) return;
    try {
      await api.post(`/approvals/${group_id}`, { approve, remark });
      toast.success(approve ? "Approved" : "Rejected");
      qc.invalidateQueries();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <>
      <p className="text-sm text-muted-foreground">Receiver entries pending your review.</p>
      {isLoading ? <div>Loading…</div> : (
        <div className="bg-white border border-border rounded-sm divide-y divide-border">
          {(data || []).length === 0 && <div className="p-6 text-sm text-muted-foreground">Inbox zero.</div>}
          {(data || []).map(g => (
            <div key={g.group_id} className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center"
                 data-testid={`approval-${g.group_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-xs text-muted-foreground">
                  {new Date(g.timestamp).toLocaleString()}
                </div>
                <div className="font-medium">{g.item_name}</div>
                <div className="container-code text-primary">{g.batch_number}</div>
                <div className="text-xs text-muted-foreground">
                  {g.container_count} container{g.container_count > 1 ? "s" : ""} · {g.total_qty} · by {g.submitted_by}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => decide(g.group_id, true)}
                        className="btn-primary" data-testid={`approve-${g.group_id}`}>
                  <Check className="w-4 h-4 mr-1" /> Approve
                </button>
                <button onClick={() => decide(g.group_id, false)}
                        className="btn-ghost text-destructive border-destructive/20 hover:bg-destructive/5"
                        data-testid={`reject-${g.group_id}`}>
                  <X className="w-4 h-4 mr-1" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function HistoryTab() {
  const [filters, setFilters] = useState({
    status: "",
    start: new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
  });
  const { data = [], isLoading } = useQuery({
    queryKey: ["approvals-history", filters],
    queryFn: async () => (await api.get("/approvals/history", {
      params: {
        status: filters.status || undefined,
        start: filters.start ? filters.start + "T00:00:00" : undefined,
        end: filters.end ? filters.end + "T23:59:59" : undefined,
      },
    })).data,
  });

  const statusBadge = (s) => {
    if (s === "pending_approval") return { cls: "bg-secondary/15 text-secondary", label: "Pending", Icon: Clock };
    if (s === "posted") return { cls: "bg-success/10 text-success", label: "Approved", Icon: CheckCircle2 };
    if (s === "rejected") return { cls: "bg-destructive/10 text-destructive", label: "Rejected", Icon: XCircle };
    return { cls: "bg-muted text-muted-foreground", label: s, Icon: Clock };
  };

  return (
    <>
      <div className="flex flex-wrap gap-3 items-end bg-white border border-border rounded-sm p-3">
        <label className="flex-1 min-w-[120px]">
          <span className="data-label">Status</span>
          <select className="field-sm mt-1" value={filters.status}
                  onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                  data-testid="history-status">
            <option value="">All</option>
            <option value="pending_approval">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </label>
        <label className="flex-1 min-w-[130px]">
          <span className="data-label">From</span>
          <input type="date" className="field-sm mt-1" value={filters.start}
                 onChange={(e) => setFilters(f => ({ ...f, start: e.target.value }))}
                 data-testid="history-start" />
        </label>
        <label className="flex-1 min-w-[130px]">
          <span className="data-label">To</span>
          <input type="date" className="field-sm mt-1" value={filters.end}
                 onChange={(e) => setFilters(f => ({ ...f, end: e.target.value }))}
                 data-testid="history-end" />
        </label>
      </div>

      {isLoading ? <div>Loading…</div> : (
        <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="history-list">
          {data.length === 0 && <div className="p-6 text-sm text-muted-foreground">No approval history in this range.</div>}
          {data.map(g => {
            const b = statusBadge(g.status);
            return (
              <div key={g.group_id} className="p-4 flex flex-col md:flex-row gap-3 md:items-center"
                   data-testid={`history-${g.group_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`badge-flat ${b.cls}`}>
                      <b.Icon className="w-3 h-3 mr-1 inline" /> {b.label}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {new Date(g.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-1 font-medium">{g.item_name}</div>
                  <div className="container-code text-primary">{g.batch_number}</div>
                  <div className="text-xs text-muted-foreground">
                    {g.container_count} container{g.container_count > 1 ? "s" : ""} · {g.total_qty}
                    {g.location_code && <> · {g.location_code}</>}
                    <> · by <span className="font-medium">{g.submitted_by}</span></>
                  </div>
                  {g.approved_by_name && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {g.status === "posted" ? "Approved" : "Rejected"} by <span className="font-medium">{g.approved_by_name}</span>
                      {g.approved_at && <> on {new Date(g.approved_at).toLocaleString()}</>}
                      {g.approval_remark && <> — <span className="italic">"{g.approval_remark}"</span></>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
